import React, { useEffect, useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const GoogleReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(null);
  const [reviewCount, setReviewCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cached, setCached] = useState(false);
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${BACKEND_URL}/api/reviews`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setReviews(data.reviews);
        setRating(data.rating);
        setReviewCount(data.review_count);
        setCached(data.cached);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load reviews');
        console.error('Error fetching reviews:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchGoogleUrl = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/reviews/google-url`);
        if (response.ok) {
          const data = await response.json();
          setGoogleMapsUrl(data.url);
        }
      } catch (err) {
        console.error('Error fetching Google Maps URL:', err);
      }
    };

    fetchReviews();
    fetchGoogleUrl();
    
    // Refresh reviews every 24 hours
    const interval = setInterval(fetchReviews, 24 * 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [BACKEND_URL]);

  const StarRating = ({ rating, size = 18 }) => (
    <div className="flex gap-1">
      {[...Array(5)].map((_, index) => (
        <Star
          key={index}
          size={size}
          fill={rating > index ? '#d4af37' : 'none'}
          color={rating > index ? '#d4af37' : '#6b7280'}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">Caricamento recensioni...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-400">Impossibile caricare le recensioni</p>
      </div>
    );
  }

  return (
    <div>
      <div className="text-center mb-8">
        {rating && (
          <div className="flex items-center justify-center gap-3 mb-2">
            <StarRating rating={rating} size={24} />
            <span className="text-2xl font-bold text-amber-500">
              {rating.toFixed(1)}
            </span>
            <span className="text-gray-400">
              ({reviewCount} recensioni)
            </span>
          </div>
        )}
        {cached && (
          <p className="text-xs text-gray-500 italic mb-3">
            Dati aggiornati giornalmente
          </p>
        )}
        
        {googleMapsUrl && (
          <Button
            onClick={() => window.open(googleMapsUrl, '_blank')}
            variant="outline"
            className="mt-4 border-2 border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black font-semibold px-6 py-2 rounded-none transition-all"
          >
            <ExternalLink className="mr-2" size={18} />
            Vedi tutte le recensioni su Google
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {reviews.length > 0 ? (
          reviews.slice(0, 8).map((review, index) => (
            <Card 
              key={index} 
              className="bg-black border-zinc-800 hover:border-amber-500 transition-all duration-300"
            >
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <span className="font-semibold text-white">
                    {review.reviewer_name}
                  </span>
                  <StarRating rating={review.rating} size={14} />
                </div>
                {review.review_text && (
                  <p className="text-gray-300 mb-3 italic leading-relaxed">
                    "{review.review_text}"
                  </p>
                )}
                <span className="text-xs text-gray-500">
                  {new Date(review.publish_time).toLocaleDateString('it-IT', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </CardContent>
            </Card>
          ))
        ) : (
          <p className="text-gray-400 col-span-2 text-center">
            Nessuna recensione disponibile
          </p>
        )}
      </div>
    </div>
  );
};

export default GoogleReviews;
